import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import Icon from '../Icon';
import { cn } from '../cn';

export type IconButtonVariant = 'ghost' | 'surface' | 'primary' | 'plain';
export type IconButtonSize = 'xs' | 'sm' | 'md' | 'lg';

const VARIANT_CLASSES: Record<IconButtonVariant, string> = {
  ghost: 'border-none bg-transparent text-accent hover:bg-surface-high',
  surface: 'border border-border bg-surface-high text-text',
  primary: 'border-none bg-accent-gradient text-on-accent shadow-lg',
  plain: 'border-none bg-transparent text-accent',
};

const SIZE_CLASSES: Record<IconButtonSize, { button: string; icon: string }> = {
  xs: { button: 'h-8 w-8', icon: 'text-[18px]' },
  sm: { button: 'h-[34px] w-[34px]', icon: 'text-[20px]' },
  md: { button: 'h-10 w-10', icon: 'text-2xl' },
  lg: { button: 'h-12 w-12', icon: 'text-2xl' },
};

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label'
> {
  /** Material Symbols ligature name. Omit when rendering custom `children` (e.g. a flag or badge icon). */
  icon?: string;
  /** Accessible name (rendered as `aria-label`). */
  label: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  iconClassName?: string;
  filled?: boolean;
  /** Content rendered after the icon (e.g. a badge), or instead of it when `icon` is omitted. */
  children?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

export default function IconButton({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  iconClassName,
  filled,
  className,
  type = 'button',
  children,
  ref,
  ...rest
}: IconButtonProps) {
  const sizeClasses = SIZE_CLASSES[size];

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      aria-label={label}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-[var(--radius-full)] p-0 cursor-pointer transition-all duration-200 active:scale-95',
        'disabled:cursor-not-allowed disabled:opacity-50',
        sizeClasses.button,
        VARIANT_CLASSES[variant],
        className,
      )}
    >
      {icon ? (
        <Icon
          name={icon}
          filled={filled ?? false}
          className={cn('!leading-none', sizeClasses.icon, iconClassName)}
        />
      ) : null}
      {children}
    </button>
  );
}
